import React from 'react';
import { createRoot } from 'react-dom/client';
// Direkt die eine Datei statt des Paket-Entrypoints: dessen `index.mjs` zieht
// die Re-Export-Zeilen aller ~6.258 Icons in den Graph (ein 500-kB-Chunk, den
// sonst niemand braucht) und macht die Icons zusätzlich statisch bekannt.
import IconQuestionMark from '@tabler/icons-react/dist/esm/icons/IconQuestionMark.mjs';
import { isCustomIconUrl, normalizeIconName } from '@core/shared';

// Die Namens- und URL-Logik (Legacy-Map, Kurz-Aliase, Pascal-Case-Fallback)
// liegt framework-frei in `@core/shared`. Hier bleibt nur der React-Loader.
export { isCustomIconUrl };

const iconCache = {};

/**
 * Die Icon-Map kommt als virtuelles Modul aus `@core/ui/vite` (eine
 * `import()`-Zeile pro Icon-Datei). Sie wird erst beim ersten Icon-Rendern
 * geladen und ist deshalb ein eigener Async-Chunk — nicht der App-Entry.
 */
let iconMapPromise = null;
const loadIconMap = () => {
    if (!iconMapPromise) {
        iconMapPromise = import('virtual:@core/ui/tabler-icons').catch((err) => {
            // Fehlschlag nicht cachen, damit ein späterer Versuch erneut lädt.
            iconMapPromise = null;
            throw err;
        });
    }

    return iconMapPromise;
};

const loadIcon = async (targetName, rawName) => {
    let icons;

    try {
        ({ icons } = await loadIconMap());
    } catch (err) {
        console.warn('Tabler-Icon-Map konnte nicht geladen werden. Verwende Fallback.', err);
        return { default: IconQuestionMark };
    }

    const load = icons[targetName];
    if (!load) {
        console.warn(`Icon module for "${targetName}" (from raw name "${rawName}") not found in icon map.`);
        return { default: IconQuestionMark };
    }

    try {
        return await load();
    } catch (err) {
        console.warn(`Icon "${targetName}" not found at path. Using fallback.`, err);
        return { default: IconQuestionMark };
    }
};

/**
 * A standalone component that lazily loads a Tabler icon by its name.
 * If `name` is an image URL (http(s):// or /...), an <img> is rendered instead.
 *
 * Sizing contract:
 * - `size` given: Tabler icons and images are rendered at exactly that size.
 * - `size` undefined: Tabler icons fall back to `defaultSize` (or 24); images
 *   render at their natural dimensions, capped by the container
 *   (max-width/max-height 100%) and clipped by its border.
 *
 * @param {string} name - The name of the icon (e.g., 'IconUser', 'leaf', legacy 'faUser') or an image URL
 * @param {number} size - Optional icon size
 * @param {number} defaultSize - Optional fallback size for Tabler icons when `size` is undefined (default: 24)
 * @param {string} className - Optional CSS classes
 */
const DynamicIcon = ({ name, size, defaultSize, className, alt, style, ...iconProps }) => {
    const resolvedSize = size || defaultSize || 24;
    const isUrl = isCustomIconUrl(name);
    const targetName = isUrl ? null : normalizeIconName(name);

    // Memoize the lazy component to prevent re-creating it on every render
    const Icon = React.useMemo(() => {
        if (!targetName) return null;

        if (!iconCache[targetName]) {
            iconCache[targetName] = React.lazy(() => loadIcon(targetName, name));
        }

        return iconCache[targetName];
    }, [targetName, name]);

    if (isUrl) {
        if (size) {
            return (
                <img
                    src={name.trim()}
                    width={size}
                    height={size}
                    className={className}
                    style={style}
                    alt={alt || ''}
                    draggable={false}
                />
            );
        }
        return (
            <img
                src={name.trim()}
                className={className}
                style={{ maxWidth: '100%', maxHeight: '100%', ...style }}
                alt={alt || ''}
                draggable={false}
            />
        );
    }

    if (!Icon) return null;

    return (
        <React.Suspense fallback={<span style={{ width: resolvedSize, height: resolvedSize }} />}>
            <Icon {...iconProps} className={className} style={style} size={resolvedSize} />
        </React.Suspense>
    );
};

/**
 * Register a Custom Element (Web Component) wrapper for DynamicIcon.
 * This allows using <tabler-icon name="IconUser" size="32" class="text-primary"></tabler-icon>
 * inside dangerouslySetInnerHTML or any static HTML strings.
 *
 * Genau eine Registrierungsstelle im Monorepo: beide Layer importieren diese
 * Datei. Die Wache hält einen zweiten Import derselben Seite am Leben.
 */
if (typeof window !== 'undefined' && !customElements.get('tabler-icon')) {
    class DynamicIconElement extends HTMLElement {
        constructor() {
            super();
            this.root = null;
        }

        connectedCallback() {
            if (!this.root) {
                this.root = createRoot(this);
            }
            this.render();
        }

        attributeChangedCallback() {
            this.render();
        }

        static get observedAttributes() {
            return ['name', 'size', 'class', 'className', 'color'];
        }

        render() {
            if (!this.root) return;

            const name = this.getAttribute('name');
            const size = this.getAttribute('size') || undefined;
            const className = this.getAttribute('class') || this.getAttribute('className') || undefined;
            const color = this.getAttribute('color') || undefined;

            this.root.render(
                <DynamicIcon
                    name={name}
                    size={size ? parseInt(size, 10) : undefined}
                    className={className}
                    color={color}
                />
            );
        }

        disconnectedCallback() {
            if (this.root) {
                // Delaying unmount to avoid race conditions during fast DOM updates
                const currentRoot = this.root;
                setTimeout(() => currentRoot.unmount(), 0);
                this.root = null;
            }
        }
    }

    customElements.define('tabler-icon', DynamicIconElement);
}

export default DynamicIcon;
